import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
    alpha,
    AppBar,
    Box,
    Chip,
    Grid,
    IconButton,
    Stack,
    Tab,
    Tabs,
    Theme,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import useSextforceAutoRepostColors from '../../../../hooks/useSextforceAutoRepostColors';
import useSextforceAutoStories, { AutoStoryLog, AutoStorySchedule } from '../../../../hooks/useSextforceAutoStories';

type Props = {
    schedules: AutoStorySchedule[];
    onEditSchedule: (schedule: AutoStorySchedule) => void;
};

type Occurrence = {
    schedule: AutoStorySchedule;
    dateTime: moment.Moment;
};

const MAX_OCCURRENCES_PER_SCHEDULE = 100;

// Projects the (up to 100) occurrences of a single schedule that fall within [rangeStart,
// rangeEnd]. One-off schedules (no repeatAmount/repeatUnit) only ever produce a single
// occurrence at nextPostDate.
const computeOccurrencesInRange = (schedule: AutoStorySchedule, rangeStart: moment.Moment, rangeEnd: moment.Moment): moment.Moment[] => {
    const occurrences: moment.Moment[] = [];

    if (!schedule.repeatAmount || !schedule.repeatUnit) {
        const dateTime = moment(schedule.nextPostDate);

        if (dateTime.isSameOrAfter(rangeStart) && dateTime.isSameOrBefore(rangeEnd)) {
            occurrences.push(dateTime);
        }

        return occurrences;
    }

    const unit = schedule.repeatUnit.toLowerCase() as 'hours' | 'days';
    let current = moment(schedule.nextPostDate);
    let steps = 0;

    while (steps < MAX_OCCURRENCES_PER_SCHEDULE && current.isSameOrBefore(rangeEnd)) {
        if (current.isSameOrAfter(rangeStart)) {
            occurrences.push(current.clone());
        }

        current = current.clone().add(schedule.repeatAmount, unit);
        steps += 1;
    }

    return occurrences;
};

const SextforceAutoStoriesCalendar = ({ schedules, onEditSchedule }: Props) => {
    const theme: Theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [currentDate, setCurrentDate] = useState<moment.Moment>(
        (localStorage.getItem('sextforceAutoStoriesCalendarViewCurrentDate') &&
            moment(localStorage.getItem('sextforceAutoStoriesCalendarViewCurrentDate'))) ||
            moment(),
    );
    const [selectedDate, setSelectedDate] = useState<moment.Moment>(
        (localStorage.getItem('sextforceAutoStoriesCalendarViewSelectedDate') &&
            moment(localStorage.getItem('sextforceAutoStoriesCalendarViewSelectedDate'))) ||
            moment(),
    );
    const [view, setView] = useState<'month' | 'day' | 'list'>(
        (localStorage.getItem('sextforceAutoStoriesCalendarView') as 'month' | 'day' | 'list') || 'month',
    );

    const { data: colors } = useSextforceAutoRepostColors();
    const { useLogs } = useSextforceAutoStories();

    const activeSchedules = useMemo(() => schedules.filter(s => s.active), [schedules]);

    const monthStart = useMemo(() => moment(currentDate).startOf('month'), [currentDate]);
    const monthEnd = useMemo(() => moment(currentDate).endOf('month'), [currentDate]);

    const listRangeStart = useMemo(() => moment().subtract(7, 'days').startOf('day'), []);
    const listRangeEnd = useMemo(() => moment().add(30, 'days').endOf('day'), []);

    const visibleRange: { start: moment.Moment; end: moment.Moment } = useMemo(() => {
        if (view === 'day') {
            return { start: moment(selectedDate).startOf('day'), end: moment(selectedDate).endOf('day') };
        }

        if (view === 'list') {
            return { start: listRangeStart, end: listRangeEnd };
        }

        return { start: monthStart.clone().startOf('day'), end: monthEnd.clone().endOf('day') };
    }, [view, selectedDate, monthStart, monthEnd, listRangeStart, listRangeEnd]);

    const { data: logs } = useLogs(visibleRange.start, visibleRange.end);

    const occurrencesInVisibleRange: Occurrence[] = useMemo(() => {
        const acc: Occurrence[] = [];

        activeSchedules.forEach(schedule => {
            computeOccurrencesInRange(schedule, visibleRange.start, visibleRange.end).forEach(dateTime => {
                acc.push({ schedule, dateTime });
            });
        });

        return acc.sort((a, b) => a.dateTime.valueOf() - b.dateTime.valueOf());
    }, [activeSchedules, visibleRange]);

    const resolveColor = (colorId: string | null): { name: string; hex: string } => {
        if (!colorId || !colors) {
            return { name: 'No category', hex: alpha(theme.palette.primary.light, 0.5) };
        }

        const found = colors.find(c => c._id === colorId);

        return found ? { name: found.name, hex: found.color } : { name: 'No category', hex: alpha(theme.palette.primary.light, 0.5) };
    };

    const handlePrevMonth = () => {
        const next = moment(currentDate).subtract(1, 'month');
        setCurrentDate(next);
        localStorage.setItem('sextforceAutoStoriesCalendarViewCurrentDate', next.format());
    };

    const handleNextMonth = () => {
        const next = moment(currentDate).add(1, 'month');
        setCurrentDate(next);
        localStorage.setItem('sextforceAutoStoriesCalendarViewCurrentDate', next.format());
    };

    const handlePrevDay = () => {
        const next = moment(selectedDate).subtract(1, 'day');
        setSelectedDate(next);
        localStorage.setItem('sextforceAutoStoriesCalendarViewSelectedDate', next.format());

        if (next.isBefore(moment(currentDate).startOf('month'))) {
            handlePrevMonth();
        }
    };

    const handleNextDay = () => {
        const next = moment(selectedDate).add(1, 'day');
        setSelectedDate(next);
        localStorage.setItem('sextforceAutoStoriesCalendarViewSelectedDate', next.format());

        if (next.isAfter(moment(currentDate).endOf('month'))) {
            handleNextMonth();
        }
    };

    const handleDateSelect = (date: moment.Moment) => {
        setSelectedDate(date);
        localStorage.setItem('sextforceAutoStoriesCalendarViewSelectedDate', date.format());
        setView('day');
        localStorage.setItem('sextforceAutoStoriesCalendarView', 'day');
    };

    const handleViewChange = (_event: React.SyntheticEvent, newValue: 'month' | 'day' | 'list') => {
        setView(newValue);
        localStorage.setItem('sextforceAutoStoriesCalendarView', newValue);
    };

    const occurrencesForDay = (day: moment.Moment): Occurrence[] =>
        occurrencesInVisibleRange.filter(occurrence => occurrence.dateTime.isSame(day, 'day'));

    const logsForDay = (day: moment.Moment): AutoStoryLog[] => (logs || []).filter(log => moment(log.postedAt).isSame(day, 'day'));

    const groupOccurrencesByColor = (occurrences: Occurrence[]): { [colorId: string]: Occurrence[] } =>
        occurrences.reduce((acc, occurrence) => {
            const key = occurrence.schedule.colorId || 'none';
            if (!acc[key]) acc[key] = [];
            acc[key].push(occurrence);

            return acc;
        }, {} as { [colorId: string]: Occurrence[] });

    const renderDayContent = (day: moment.Moment) => {
        const dayOccurrences = occurrencesForDay(day);
        const dayLogs = logsForDay(day);
        const grouped = groupOccurrencesByColor(dayOccurrences);
        const failedCount = dayLogs.filter(l => l.status === 'failed').length;
        const postedCount = dayLogs.filter(l => l.status === 'posted').length;

        return (
            <Stack direction="column" spacing={0.5} alignItems="center">
                {Object.entries(grouped).map(([colorId, occurrences]) => {
                    const { name, hex } = resolveColor(colorId === 'none' ? null : colorId);

                    return (
                        <Tooltip
                            key={colorId}
                            title={
                                isMobile
                                    ? `${name} (${occurrences.length})`
                                    : `${name}: ${occurrences.map(o => o.dateTime.format('LT')).join(', ')}`
                            }
                        >
                            <Box
                                sx={{
                                    pl: 0.5,
                                    pr: 0.5,
                                    backgroundColor: hex,
                                    borderRadius: theme.shape.borderRadius,
                                    width: '100%',
                                }}
                            >
                                <Typography variant="caption" color={theme.palette.getContrastText(hex)}>
                                    {occurrences.length} {!isMobile && `stor${occurrences.length > 1 ? 'ies' : 'y'}`}
                                </Typography>
                            </Box>
                        </Tooltip>
                    );
                })}
                {(postedCount > 0 || failedCount > 0) && (
                    <Stack direction="row" spacing={0.5}>
                        {postedCount > 0 && (
                            <Tooltip title={`${postedCount} posted`}>
                                <Stack direction="row" spacing={0.25} alignItems="center">
                                    <CheckCircleIcon color="success" sx={{ fontSize: 14 }} />
                                    {!isMobile && <Typography variant="caption">{postedCount}</Typography>}
                                </Stack>
                            </Tooltip>
                        )}
                        {failedCount > 0 && (
                            <Tooltip title={`${failedCount} failed`}>
                                <Stack direction="row" spacing={0.25} alignItems="center">
                                    <CancelIcon color="error" sx={{ fontSize: 14 }} />
                                    {!isMobile && <Typography variant="caption">{failedCount}</Typography>}
                                </Stack>
                            </Tooltip>
                        )}
                    </Stack>
                )}
            </Stack>
        );
    };

    const renderMonthView = () => {
        const firstDayOfMonth = monthStart.day();
        const days: (moment.Moment | null)[] = Array(firstDayOfMonth).fill(null);

        let currentDay = monthStart.clone();
        while (currentDay.isSameOrBefore(monthEnd)) {
            days.push(currentDay.clone());
            currentDay = currentDay.clone().add(1, 'day');
        }

        const remainingDays = (7 - (days.length % 7)) % 7;
        days.push(...Array(remainingDays).fill(null));

        const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        return (
            <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconButton onClick={handlePrevMonth}>
                        <ChevronLeft />
                    </IconButton>
                    <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
                        {currentDate.format('MMMM YYYY')}
                    </Typography>
                    <IconButton onClick={handleNextMonth}>
                        <ChevronRight />
                    </IconButton>
                </Box>

                <Grid container spacing={0}>
                    {weekDays.map(day => (
                        <Grid item key={day} xs={12 / 7}>
                            <Typography align="center" variant="subtitle2">
                                {isMobile ? day.substring(0, 1) : day}
                            </Typography>
                        </Grid>
                    ))}

                    {days.map((day, index) => (
                        <Grid
                            item
                            xs={12 / 7}
                            key={index}
                            sx={{
                                borderTop: index < 7 ? `1px solid ${theme.palette.divider}` : 'none',
                                borderLeft: index % 7 !== 0 ? `1px solid ${theme.palette.divider}` : 'none',
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            {day ? (
                                <Box
                                    onClick={() => handleDateSelect(day)}
                                    sx={{
                                        p: 0.5,
                                        minHeight: 80,
                                        height: '100%',
                                        cursor: 'pointer',
                                        backgroundColor: 'background.paper',
                                        '&:hover': { backgroundColor: 'action.hover' },
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        align="center"
                                        sx={{ fontWeight: moment().isSame(day, 'day') ? 'bold' : 'normal' }}
                                    >
                                        {day.format('D')}
                                    </Typography>
                                    {renderDayContent(day)}
                                </Box>
                            ) : (
                                <Box sx={{ p: 1, minHeight: 80, height: '100%', backgroundColor: 'action.disabledBackground' }} />
                            )}
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    };

    const renderOccurrenceRow = (occurrence: Occurrence, key: string) => {
        const { name, hex } = resolveColor(occurrence.schedule.colorId);

        return (
            <Box
                key={key}
                onClick={() => onEditSchedule(occurrence.schedule)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    borderRadius: theme.shape.borderRadius,
                    cursor: 'pointer',
                    border: `1px solid ${theme.palette.divider}`,
                    '&:hover': { backgroundColor: 'action.hover' },
                }}
            >
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: hex, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ minWidth: 70 }}>
                    {occurrence.dateTime.format('LT')}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                    {occurrence.schedule.name}
                </Typography>
                <Chip size="small" label={occurrence.schedule.mode === 'MANUAL' ? 'Manual' : 'Album'} />
                <Chip size="small" variant="outlined" label={name} sx={{ borderColor: hex }} />
            </Box>
        );
    };

    const renderLogRow = (log: AutoStoryLog, key: string) => (
        <Box
            key={key}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: theme.shape.borderRadius,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: log.status === 'failed' ? alpha(theme.palette.error.light, 0.1) : 'transparent',
            }}
        >
            {log.status === 'failed' ? (
                <Tooltip title={log.error || 'Failed'}>
                    <CancelIcon color="error" fontSize="small" />
                </Tooltip>
            ) : (
                <CheckCircleIcon color="success" fontSize="small" />
            )}
            <Typography variant="body2" sx={{ minWidth: 70 }}>
                {moment(log.postedAt).format('LT')}
            </Typography>
            <Typography variant="body2" sx={{ flex: 1 }} color="text.secondary">
                {log.status === 'failed' ? log.error || 'Failed to post' : 'Posted'}
            </Typography>
        </Box>
    );

    const renderDayView = () => {
        const dayOccurrences = occurrencesForDay(selectedDate);
        const dayLogs = logsForDay(selectedDate);

        return (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconButton onClick={handlePrevDay}>
                        <ChevronLeft />
                    </IconButton>
                    <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
                        {selectedDate.format('dddd, MMMM D, YYYY')}
                    </Typography>
                    <IconButton onClick={handleNextDay}>
                        <ChevronRight />
                    </IconButton>
                </Box>

                <Stack spacing={1}>
                    {dayOccurrences.length === 0 && dayLogs.length === 0 && (
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                            No scheduled or posted stories on this day
                        </Typography>
                    )}
                    {dayOccurrences.map((occurrence, index) =>
                        renderOccurrenceRow(occurrence, `${occurrence.schedule.id}-${occurrence.dateTime.valueOf()}-${index}`),
                    )}
                    {dayLogs.map((log, index) => renderLogRow(log, `${log.id}-${index}`))}
                </Stack>
            </Box>
        );
    };

    const renderListView = () => (
        <Stack spacing={1}>
            {occurrencesInVisibleRange.length === 0 && (logs || []).length === 0 && (
                <Typography variant="body1" align="center" color="text.secondary" sx={{ py: 2 }}>
                    No scheduled or posted stories in the next 30 days
                </Typography>
            )}
            {occurrencesInVisibleRange.map((occurrence, index) =>
                renderOccurrenceRow(occurrence, `${occurrence.schedule.id}-${occurrence.dateTime.valueOf()}-${index}`),
            )}
            {(logs || [])
                .slice()
                .sort((a, b) => moment(b.postedAt).valueOf() - moment(a.postedAt).valueOf())
                .map((log, index) => renderLogRow(log, `${log.id}-${index}`))}
        </Stack>
    );

    return (
        <Box>
            <AppBar position="static" sx={{ mb: 2 }}>
                <Tabs indicatorColor="secondary" textColor="inherit" variant="fullWidth" value={view} onChange={handleViewChange} centered>
                    <Tab label="Month" value="month" />
                    <Tab label="Day" value="day" />
                    <Tab label="List" value="list" />
                </Tabs>
            </AppBar>

            {view === 'month' && renderMonthView()}
            {view === 'day' && renderDayView()}
            {view === 'list' && renderListView()}
        </Box>
    );
};

export default SextforceAutoStoriesCalendar;
